import AgoraRTC from "agora-rtc-sdk-ng";
import { CaptureController } from "./capture-controller.js";
import { collectAudioQuality, createAudioQualityReporter } from "./rtc-audio-quality.js";
import {
  restoreVideoAfterBackground,
  suspendVideoForBackground
} from "./call-media-lifecycle.js";
import {
  alphaCallLimitNotice,
  callStartFailureMessage,
  callLimitWarning,
  classifyClientError,
  clientPlatform,
  POST_CALL_PROCESSING_ESTIMATE,
  maximumDurationEndCopy,
  preferredVideoCodec,
  recordingStatusLabel,
  shouldShowPostCallProcessingEstimate,
  shouldOfferMediaRecovery
} from "./call-runtime-policy.js";

const views = {
  loading: document.querySelector("#loading-view"),
  consent: document.querySelector("#consent-view"),
  lobby: document.querySelector("#lobby-view"),
  call: document.querySelector("#call-view"),
  ended: document.querySelector("#ended-view"),
  error: document.querySelector("#error-view")
};

const nodes = {
  consentIdentity: document.querySelector("#consent-identity"),
  consentForm: document.querySelector("#consent-form"),
  consentCheck: document.querySelector("#consent-check"),
  openPrivacy: document.querySelector("#open-privacy"),
  consentSubmit: document.querySelector("#consent-submit"),
  consentError: document.querySelector("#consent-error"),
  lobbyIdentity: document.querySelector("#lobby-identity"),
  lobbyTitle: document.querySelector("#lobby-title"),
  lobbyHint: document.querySelector("#lobby-hint"),
  alphaCallLimitNotice: document.querySelector("#alpha-call-limit-notice"),
  invitationWaiting: document.querySelector("#invitation-waiting"),
  roomTrial: document.querySelector("#room-trial"),
  roomTrialStatus: document.querySelector("#room-trial-status"),
  roomTrialTerms: document.querySelector("#room-trial-terms"),
  roomTrialAcceptRow: document.querySelector("#room-trial-accept-row"),
  roomTrialAccept: document.querySelector("#room-trial-accept"),
  roomTrialClaim: document.querySelector("#room-trial-claim"),
  roomTrialError: document.querySelector("#room-trial-error"),
  enterCall: document.querySelector("#enter-call"),
  remotePlayers: document.querySelector("#remote-players"),
  waitingOverlay: document.querySelector("#waiting-overlay"),
  recordingBadge: document.querySelector("#recording-badge"),
  recordingLabel: document.querySelector("#recording-label"),
  captureBadge: document.querySelector("#capture-badge"),
  captureLabel: document.querySelector("#capture-label"),
  duration: document.querySelector("#duration"),
  connectionLabel: document.querySelector("#connection-label"),
  localPlayer: document.querySelector("#local-player"),
  resumeMedia: document.querySelector("#resume-media"),
  callMessage: document.querySelector("#call-message"),
  toggleMic: document.querySelector("#toggle-mic"),
  toggleCamera: document.querySelector("#toggle-camera"),
  leaveCall: document.querySelector("#leave-call"),
  endedTitle: document.querySelector("#ended-title"),
  endedMessage: document.querySelector("#ended-message"),
  endedEstimate: document.querySelector("#ended-estimate"),
  errorMessage: document.querySelector("#error-message"),
  retryCall: document.querySelector("#retry-call"),
  returnToday: document.querySelector("#return-today")
};

const detectedPlatform = clientPlatform(globalThis.navigator);

const state = {
  inviteToken: "",
  bridgeToken: "",
  resumeToken: "",
  consentVersion: "",
  consentPurposeHash: "",
  trialTermsVersion: null,
  lobbyTimer: null,
  lobbyBlocked: false,
  view: "loading",
  participant: null,
  client: null,
  localAudioTrack: null,
  localVideoTrack: null,
  callStartedAt: null,
  recordingStartedAtMs: null,
  callEndsAtMs: null,
  rtcRenewalDeadlineMs: null,
  serverClockOffsetMs: 0,
  alphaCompleteExperienceEnabled: false,
  callMaxSeconds: null,
  callLimitWarningsShown: new Set(),
  durationTimer: null,
  heartbeatTimer: null,
  statusTimer: null,
  reconnectTimer: null,
  reconnectAttempts: 0,
  backgroundedAt: null,
  videoSuspendedForBackground: false,
  videoSuspendPromise: null,
  foregroundRecoveryPromise: null,
  leaving: false,
  micEnabled: true,
  cameraEnabled: true,
  remoteVideoUids: new Set(),
  remoteAudioUids: new Set(),
  remoteVolumeLevels: new Map(),
  audioContextState: "unknown",
  autoplayBlocked: false,
  callActive: false,
  platform: detectedPlatform,
  ios: detectedPlatform === "ios_webview",
  captureController: null,
  captureState: "disabled",
  h5CaptureEnabled: false
};

function showView(name) {
  state.view = name;
  clearTimeout(state.lobbyTimer);
  if (name === "lobby") scheduleLobbyRefresh();
  for (const [key, view] of Object.entries(views)) {
    view.hidden = key !== name;
  }
}

function scheduleLobbyRefresh() {
  clearTimeout(state.lobbyTimer);
  state.lobbyTimer = setTimeout(async () => {
    if (state.view !== "lobby" || state.leaving) return;
    try {
      if (document.visibilityState !== "hidden") await refreshRoomTrial();
    } catch (error) {
      nodes.enterCall.disabled = true;
      if (nodes.invitationWaiting) {
        nodes.invitationWaiting.hidden = false;
        nodes.invitationWaiting.textContent = error.message || "暂时无法刷新邀请状态，正在重试。";
      }
    } finally {
      if (state.view === "lobby" && !state.leaving) scheduleLobbyRefresh();
    }
  }, 3000);
}

async function api(path, options = {}) {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {})
    }
  });
  const body = response.status === 204
    ? null
    : await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body?.error || "请求失败，请稍后重试。");
    error.code = body?.code || `HTTP_${response.status}`;
    error.status = response.status;
    throw error;
  }
  if (path === "/api/rtc-token") {
    const deadline = Date.parse(body?.renewalDeadlineAt || "");
    state.rtcRenewalDeadlineMs = Number.isFinite(deadline) ? deadline : null;
    const serverTime = Date.parse(body?.serverTime || "");
    if (Number.isFinite(serverTime)) state.serverClockOffsetMs = serverTime - Date.now();
  }
  return body;
}

function showFatalError(message, { retryCall = false } = {}) {
  nodes.errorMessage.textContent = message;
  nodes.retryCall.hidden = !retryCall;
  showView("error");
}

function setCallMessage(message, kind = "") {
  nodes.callMessage.textContent = message;
  nodes.callMessage.dataset.kind = kind;
}

function formatDuration(milliseconds) {
  const total = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return hours > 0
    ? `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    : `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function startDurationTimer() {
  if (!state.callStartedAt) state.callStartedAt = Date.now();
  clearInterval(state.durationTimer);
  const update = () => {
    const serverNow = Date.now() + state.serverClockOffsetMs;
    const elapsedMilliseconds = state.recordingStartedAtMs
      ? serverNow - state.recordingStartedAtMs
      : Date.now() - state.callStartedAt;
    nodes.duration.textContent = formatDuration(elapsedMilliseconds);
    if (
      state.alphaCompleteExperienceEnabled &&
      state.callEndsAtMs &&
      !state.leaving
    ) {
      const remainingMilliseconds = state.callEndsAtMs - serverNow;
      const warning = callLimitWarning({
        remainingMilliseconds,
        shown: [...state.callLimitWarningsShown]
      });
      if (warning) {
        state.callLimitWarningsShown.add(warning.key);
        setCallMessage(warning.message, "warning");
      }
      if (remainingMilliseconds <= 0) {
        const copy = maximumDurationEndCopy(state.callMaxSeconds);
        void endCall({ ...copy, notifyLeave: false });
      }
    }
  };
  update();
  state.durationTimer = setInterval(update, 1000);
}

function setRecordingState(recordingStatus) {
  nodes.recordingBadge.dataset.state = recordingStatus;
  nodes.recordingLabel.textContent = recordingStatusLabel(recordingStatus);
}

function setCaptureState(snapshot) {
  const stateName = snapshot?.state || "preparing";
  if (stateName === "disabled") {
    state.captureState = stateName;
    nodes.captureBadge.hidden = true;
    return;
  }
  nodes.captureBadge.hidden = false;
  const labels = {
    idle: "本机副本准备中",
    probing: "本机副本准备中",
    recording: "本机副本录制中",
    uploading: "本机副本同步中",
    synced: "本机副本已同步",
    pending_upload: "本机副本待续传，云端仍在保存",
    unsupported: "本机副本不支持，云端仍在保存",
    degraded: "本机副本不可用，云端仍在保存",
    blocked: "本机副本已停止",
    stopped: "本机副本已停止"
  };
  state.captureState = stateName;
  nodes.captureBadge.dataset.state = stateName;
  nodes.captureLabel.textContent = labels[stateName] || labels.degraded;
}

function setConnectionLabel(text, quality = "") {
  nodes.connectionLabel.textContent = text;
  nodes.connectionLabel.dataset.quality = quality;
}

function addRemotePlayer(user) {
  const id = `remote-${user.uid}`;
  let node = document.getElementById(id);
  if (!node) {
    node = document.createElement("div");
    node.id = id;
    node.className = "remote-player";
    node.dataset.uid = String(user.uid);
    nodes.remotePlayers.append(node);
  }
  return node;
}

function removeRemotePlayer(uid) {
  state.remoteVideoUids.delete(String(uid));
  document.getElementById(`remote-${uid}`)?.remove();
  nodes.waitingOverlay.hidden = state.remoteVideoUids.size > 0;
}

async function forceVideoPlayback(video) {
  video.muted = true;
  video.defaultMuted = true;
  video.autoplay = true;
  video.playsInline = true;
  video.setAttribute("muted", "");
  video.setAttribute("autoplay", "");
  video.setAttribute("playsinline", "");
  video.setAttribute("webkit-playsinline", "");
  video.controls = false;
  try {
    await video.play();
    return true;
  } catch {
    return false;
  }
}

function reinforceLocalPreview() {
  const apply = () => {
    const video = nodes.localPlayer.querySelector("video");
    if (!video) return false;
    void forceVideoPlayback(video);
    video.classList.add("local-video-element");
    return true;
  };
  if (apply()) return;
  requestAnimationFrame(() => {
    if (!apply()) setTimeout(apply, 100);
  });
}

async function playLocalPreview() {
  state.localVideoTrack.play(nodes.localPlayer, {
    mirror: true,
    fit: "cover"
  });
  reinforceLocalPreview();
}

async function resumeAllMedia() {
  nodes.resumeMedia.hidden = true;
  state.autoplayBlocked = false;
  await recoverForegroundMedia();
}

function remoteTrackCount() {
  return (state.client?.remoteUsers || []).reduce(
    (count, user) => count + Number(Boolean(user.audioTrack)) +
      Number(Boolean(user.videoTrack)),
    0
  );
}

function refreshMediaRecoveryButton(message = "") {
  const visible = shouldOfferMediaRecovery({
    callActive: state.callActive,
    remoteTrackCount: remoteTrackCount(),
    autoplayBlocked: state.autoplayBlocked,
    audioContextState: state.audioContextState
  });
  nodes.resumeMedia.hidden = !visible;
  if (visible && message) setCallMessage(message, "warning");
}

function sendClientDiagnostic(stage, code) {
  void fetch("/api/client-diagnostics", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      stage,
      code,
      platform: state.platform
    }),
    credentials: "same-origin",
    keepalive: true
  }).catch(() => undefined);
}

function reportClientDiagnostic(stage, error) {
  const code = classifyClientError(error);
  sendClientDiagnostic(stage, code);
  return code;
}

async function clientCodec() {
  let supportedVideoCodecs = [];
  try {
    const supported = await AgoraRTC.getSupportedCodec();
    supportedVideoCodecs = supported?.video || [];
  } catch {
    // Codec probing is advisory; the platform default remains available.
  }
  return preferredVideoCodec({ ios: state.ios, supportedVideoCodecs });
}

async function playRemoteAudio(user) {
  if (!user.audioTrack) return;
  const uid = String(user.uid);
  user.audioTrack.setVolume(100);
  try {
    // Chrome desktop can retain a stale output route after Bluetooth/AirPlay
    // changes. Pinning to the browser's current default device is harmless on
    // the normal path and is ignored on unsupported mobile browsers.
    await user.audioTrack.setPlaybackDevice("default");
  } catch {
    // Safari and several WebViews intentionally do not support sink selection.
  }
  AgoraRTC.resumeAudioContext();
  user.audioTrack.play();
  state.remoteAudioUids.add(uid);
}

function registerClientEvents(client) {
  client.on("user-published", async (user, mediaType) => {
    try {
      await client.subscribe(user, mediaType);
      if (mediaType === "video" && user.videoTrack) {
        state.remoteVideoUids.add(String(user.uid));
        user.videoTrack.play(addRemotePlayer(user), { fit: "cover" });
        nodes.waitingOverlay.hidden = true;
        setCallMessage("");
      }
      if (mediaType === "audio" && user.audioTrack) {
        await playRemoteAudio(user);
      }
      refreshMediaRecoveryButton();
    } catch (error) {
      state.autoplayBlocked = true;
      reportClientDiagnostic("remote_playback", error);
      refreshMediaRecoveryButton("对方画面或声音被暂停了，请点一下恢复。");
    }
  });

  client.on("user-unpublished", (user, mediaType) => {
    if (mediaType === "video") {
      removeRemotePlayer(user.uid);
      if (state.remoteAudioUids.has(String(user.uid))) {
        setCallMessage("对方暂时关闭了画面，声音仍可继续。", "warning");
      }
    }
    if (mediaType === "audio") {
      state.remoteAudioUids.delete(String(user.uid));
      state.remoteVolumeLevels.delete(String(user.uid));
    }
    refreshMediaRecoveryButton();
  });

  client.on("user-left", (user) => {
    removeRemotePlayer(user.uid);
    state.remoteAudioUids.delete(String(user.uid));
    state.remoteVolumeLevels.delete(String(user.uid));
    setCallMessage("对方暂时离开，30 秒内回来会继续本次录制。", "warning");
    refreshMediaRecoveryButton();
  });

  client.on("volume-indicator", (volumes) => {
    for (const item of volumes) {
      state.remoteVolumeLevels.set(String(item.uid), item.level);
    }
  });

  client.on("connection-state-change", (current, previous, reason) => {
    if (current === "CONNECTED") {
      setConnectionLabel("已连接", "good");
      state.reconnectAttempts = 0;
      setCallMessage("");
      if (previous === "RECONNECTING" || previous === "DISCONNECTED") {
        void announceReady();
      }
    } else if (current === "RECONNECTING") {
      setConnectionLabel("网络重连中", "warning");
      setCallMessage("网络有波动，正在自动恢复通话。", "warning");
    } else if (current === "DISCONNECTED" && !state.leaving) {
      setConnectionLabel("连接已断开", "bad");
      scheduleManualReconnect(reason);
    }
  });

  client.on("media-reconnect-start", () => {
    setConnectionLabel("媒体恢复中", "warning");
  });
  client.on("media-reconnect-end", () => {
    setConnectionLabel("已连接", "good");
  });

  client.on("network-quality", (quality) => {
    const worst = Math.max(
      quality.uplinkNetworkQuality,
      quality.downlinkNetworkQuality
    );
    if (worst <= 2) setConnectionLabel("网络良好", "good");
    else if (worst <= 4) setConnectionLabel("网络一般", "warning");
    else setConnectionLabel("网络较差", "bad");
  });

  client.on("token-privilege-will-expire", async () => {
    // A bounded final token must expire naturally, not recursively renew to the same deadline.
    if (state.rtcRenewalDeadlineMs &&
      state.rtcRenewalDeadlineMs - (Date.now() + state.serverClockOffsetMs) <= 30_000) return;
    try {
      const session = await api("/api/rtc-token", {
        method: "POST",
        body: "{}"
      });
      await client.renewToken(session.token);
    } catch {
      setCallMessage("通话凭证续期失败，正在重新连接。", "warning");
    }
  });

  client.on("token-privilege-did-expire", () => {
    void recoverExpiredToken();
  });
}

async function announceReady() {
  try {
    const snapshot = await api("/api/presence/ready", {
      method: "POST",
      body: "{}"
    });
    applyRoomStatus(snapshot);
  } catch (error) {
    setCallMessage(error.message, "warning");
  }
}

async function publishLocalTracks() {
  try {
    await state.client.publish([
      state.localAudioTrack,
      state.localVideoTrack
    ]);
  } catch (error) {
    const message = `${error?.code || ""} ${error?.message || ""}`;
    if (!message.includes("ALREADY_PUBLISHED")) throw error;
  }
}

async function replayRemoteMedia() {
  let firstError = null;
  for (const user of state.client?.remoteUsers || []) {
    try {
      if (user.videoTrack) {
        state.remoteVideoUids.add(String(user.uid));
        user.videoTrack.play(addRemotePlayer(user), { fit: "cover" });
        nodes.waitingOverlay.hidden = true;
      }
      if (user.audioTrack) await playRemoteAudio(user);
    } catch (error) {
      firstError ||= error;
    }
  }
  return firstError;
}

function suspendActiveVideoForBackground() {
  if (!state.client || state.leaving || !state.callActive) return;
  state.backgroundedAt ||= Date.now();
  if (
    state.videoSuspendPromise ||
    !state.cameraEnabled ||
    !state.localVideoTrack
  ) {
    return;
  }
  state.videoSuspendedForBackground = true;
  state.videoSuspendPromise = suspendVideoForBackground({
    callActive: state.callActive,
    cameraEnabled: state.cameraEnabled,
    videoTrack: state.localVideoTrack
  })
    .then((suspended) => {
      if (suspended) {
        sendClientDiagnostic(
          "lifecycle_background",
          "MEDIA_BACKGROUND_SUSPENDED"
        );
      }
      return suspended;
    })
    .catch((error) => {
      reportClientDiagnostic("lifecycle_background", error);
      return false;
    });
}

async function recoverForegroundMedia() {
  if (!state.client || state.leaving || !state.callActive) return false;
  if (state.foregroundRecoveryPromise) return state.foregroundRecoveryPromise;

  state.foregroundRecoveryPromise = (async () => {
    setConnectionLabel("正在恢复画面", "warning");
    let playbackError = null;
    try {
      await Promise.resolve(AgoraRTC.resumeAudioContext());
    } catch (error) {
      playbackError = error;
    }

    await state.videoSuspendPromise;
    const restoredVideo = await restoreVideoAfterBackground({
      callActive: state.callActive,
      cameraEnabled: state.cameraEnabled,
      pageWasBackgrounded: state.videoSuspendedForBackground,
      videoTrack: state.localVideoTrack
    });
    if (restoredVideo) {
      // setEnabled(false -> true) makes the SDK emit the corresponding
      // unpublished/published lifecycle. Publishing the same local tracks
      // again here can create a duplicate-track error on mobile WebViews.
      await playLocalPreview();
    } else {
      reinforceLocalPreview();
    }

    const remotePlaybackError = await replayRemoteMedia();
    playbackError ||= remotePlaybackError;
    state.videoSuspendedForBackground = false;
    state.videoSuspendPromise = null;
    state.backgroundedAt = null;
    await announceReady();
    await refreshRoomStatus();

    if (playbackError) {
      state.autoplayBlocked = true;
      reportClientDiagnostic("lifecycle_foreground", playbackError);
      refreshMediaRecoveryButton(
        "画面或声音还没有完全恢复，请点一下恢复。"
      );
      return false;
    }

    state.autoplayBlocked = false;
    nodes.resumeMedia.hidden = true;
    setConnectionLabel("已连接", "good");
    setCallMessage("");
    sendClientDiagnostic(
      "lifecycle_foreground",
      "MEDIA_FOREGROUND_RECOVERED"
    );
    return true;
  })()
    .catch((error) => {
      state.autoplayBlocked = true;
      reportClientDiagnostic("lifecycle_foreground", error);
      refreshMediaRecoveryButton(
        "自动恢复没有完成，请点一下恢复画面和声音。"
      );
      return false;
    })
    .finally(() => {
      state.foregroundRecoveryPromise = null;
    });

  return state.foregroundRecoveryPromise;
}

function scheduleManualReconnect(reason) {
  if (state.leaving || state.reconnectTimer) return;
  const delay = Math.min(1000 * 2 ** state.reconnectAttempts, 10_000);
  state.reconnectAttempts += 1;
  if (state.reconnectAttempts > 5) {
    void endCall({
      title: "网络连接失败",
      message: "多次重连没有成功，请重新打开有效会话或联系发起人。",
      notifyLeave: false
    });
    return;
  }
  setCallMessage(
    `连接中断，${Math.ceil(delay / 1000)} 秒后自动重试。`,
    "warning"
  );
  state.reconnectTimer = setTimeout(async () => {
    state.reconnectTimer = null;
    try {
      const session = await api("/api/rtc-token", {
        method: "POST",
        body: "{}"
      });
      if (state.client?.connectionState !== "DISCONNECTED") {
        if (reason === "TOKEN_EXPIRED") {
          await state.client.renewToken(session.token);
          await announceReady();
        }
        return;
      }
      await state.client.join(
        session.appId,
        session.channel,
        session.token,
        session.uid
      );
      await publishLocalTracks();
      await announceReady();
    } catch {
      scheduleManualReconnect(reason);
    }
  }, delay);
}

async function recoverExpiredToken() {
  if (state.leaving) return;
  try {
    const session = await api("/api/rtc-token", {
      method: "POST",
      body: "{}"
    });
    if (
      state.client.connectionState === "CONNECTED" ||
      state.client.connectionState === "RECONNECTING"
    ) {
      await state.client.renewToken(session.token);
    } else {
      await state.client.join(
        session.appId,
        session.channel,
        session.token,
        session.uid
      );
      await publishLocalTracks();
    }
    await announceReady();
  } catch {
    scheduleManualReconnect("TOKEN_EXPIRED");
  }
}

function applyRoomStatus(snapshot) {
  if (!snapshot) return;
  const serverTimeMs = Date.parse(snapshot.serverTime || "");
  if (Number.isFinite(serverTimeMs)) {
    state.serverClockOffsetMs = serverTimeMs - Date.now();
  }
  state.alphaCompleteExperienceEnabled =
    snapshot.alphaCompleteExperienceEnabled === true;
  state.callMaxSeconds = Number(snapshot.callMaxSeconds) > 0
    ? Number(snapshot.callMaxSeconds)
    : null;
  const recordingStartedAtMs = Date.parse(snapshot.recordingStartedAt || "");
  if (Number.isFinite(recordingStartedAtMs)) {
    state.recordingStartedAtMs = recordingStartedAtMs;
  }
  const callEndsAtMs = Date.parse(snapshot.callEndsAt || "");
  state.callEndsAtMs = Number.isFinite(callEndsAtMs) ? callEndsAtMs : null;
  if (state.rtcRenewalDeadlineMs !== null && Number.isFinite(callEndsAtMs)) {
    // Once recording starts, replace the waiting deadline with its fixed end.
    state.rtcRenewalDeadlineMs = callEndsAtMs;
  }
  setRecordingState(snapshot.recordingStatus);
  if (snapshot.endReason === "maximum_duration" && !state.leaving) {
    const copy = maximumDurationEndCopy(
      snapshot.callMaxSeconds || state.callMaxSeconds
    );
    void endCall({ ...copy, notifyLeave: false });
    return;
  }
  if (snapshot.roomStatus === "cancelled" && !state.leaving) {
    void endCall({
      title: "这次邀请已经结束",
      message: "你可以回到小程序，重新发起一次通话。",
      notifyLeave: false
    });
    return;
  }
  if (snapshot.participantCount < 2) {
    nodes.waitingOverlay.hidden = false;
  }
  if (snapshot.recordingStatus === "failed" && !state.leaving) {
    void endCall({
      title: "录制没有成功",
      message:
        "为了避免出现“以为已经保存、实际没有保存”的情况，本次采集已经停止。请联系发起人后重新创建通话。",
      notifyLeave: true
    });
  }
}

async function refreshRoomStatus() {
  try {
    applyRoomStatus(await api("/api/room-status"));
  } catch (error) {
    if (error.status === 401) {
      void endCall({
        title: "会话已经过期",
        message: "请联系发起人获取新的邀请链接。",
        notifyLeave: false
      });
      return;
    }
    if (error.status === 423) {
      void endCall({
        title: "这段通话已暂停",
        message: "这段记录正在处理数据权利请求，画面、声音与录制均已停止。",
        notifyLeave: false,
        purgeCapture: true
      });
    }
  }
}

const audioQualityReporter = createAudioQualityReporter({
  read: () => state.callActive && state.client && !state.leaving ? collectAudioQuality({
    client: state.client, platform: state.platform,
    elapsedSeconds: (Date.now() - state.callStartedAt) / 1000,
    visibility: document.visibilityState, micEnabled: state.micEnabled,
    audioContext: state.audioContextState, autoplayBlocked: state.autoplayBlocked
  }) : null,
  send: async (report, signal) => {
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal.addEventListener("abort", abort, { once: true });
    const timeout = setTimeout(abort, 5000);
    try {
      if (signal.aborted) abort();
      await fetch("/api/client-audio-quality", { method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(report), signal: controller.signal });
    } finally { clearTimeout(timeout); signal.removeEventListener("abort", abort); }
  }
});

function startBackgroundTasks() {
  clearInterval(state.heartbeatTimer);
  clearInterval(state.statusTimer);
  state.heartbeatTimer = setInterval(() => {
    void api("/api/presence/heartbeat", {
      method: "POST",
      body: "{}"
    }).catch(() => undefined);
  }, 5000);
  state.statusTimer = setInterval(refreshRoomStatus, 2000);
  audioQualityReporter.start();
}

async function startCall() {
  if (state.client || state.leaving) return;
  // Recheck before microphone/RTC work. An invitation may still be pending or
  // have been cancelled since the last lobby refresh.
  try {
    await refreshRoomTrial();
    if (state.lobbyBlocked) { showView("lobby"); return; }
  } catch (error) {
    showView("lobby");
    nodes.enterCall.disabled = true;
    nodes.roomTrialError.textContent = error.message || "暂时无法核对邀请状态。";
    return;
  }
  let stage = "system_check";
  nodes.enterCall.disabled = true;
  nodes.retryCall.hidden = true;
  nodes.resumeMedia.hidden = true;
  state.autoplayBlocked = false;
  state.callActive = false;
  state.backgroundedAt = null;
  state.videoSuspendedForBackground = false;
  state.videoSuspendPromise = null;
  showView("call");
  setConnectionLabel("正在申请权限", "warning");
  setRecordingState("waiting");
  nodes.waitingOverlay.hidden = false;

  try {
    if (!AgoraRTC.checkSystemRequirements()) {
      const error = new Error("SYSTEM_NOT_SUPPORTED");
      error.code = "SYSTEM_NOT_SUPPORTED";
      throw error;
    }
    await Promise.resolve(AgoraRTC.resumeAudioContext()).catch(() => undefined);
    stage = "media_capture";
    [state.localAudioTrack, state.localVideoTrack] =
      await AgoraRTC.createMicrophoneAndCameraTracks(
        {
          AEC: true,
          AGC: true,
          ANS: true,
          encoderConfig: "speech_standard",
          bypassWebAudio: state.ios
        },
        {
          encoderConfig: "480p_1",
          facingMode: "user"
        }
      );
    state.localAudioTrack.on("track-ended", () => {
      state.captureController?.handleTrackEnded();
      setCallMessage("麦克风已停止，请检查系统权限或设备连接。", "warning");
    });
    state.localVideoTrack.on("track-ended", () => {
      state.captureController?.handleTrackEnded();
      setCallMessage("摄像头已停止，请检查系统权限或设备连接。", "warning");
    });
    stage = "local_preview";
    await playLocalPreview();

    stage = "rtc_token";
    const session = await api("/api/rtc-token", {
      method: "POST",
      body: "{}"
    });
    const client = AgoraRTC.createClient({
      mode: "rtc",
      codec: await clientCodec()
    });
    state.client = client;
    registerClientEvents(client);
    setConnectionLabel("正在进入通话", "warning");
    stage = "rtc_join";
    await client.join(
      session.appId,
      session.channel,
      session.token,
      session.uid
    );
    client.enableAudioVolumeIndicator();
    stage = "rtc_publish";
    await publishLocalTracks();
    state.callActive = true;
    state.callStartedAt = Date.now();
    startDurationTimer();
    startBackgroundTasks();
    if (state.h5CaptureEnabled) {
      state.captureController = new CaptureController({
        onState: setCaptureState
      });
      // Do not await the second-copy path before announcing presence. Agora RTC
      // and cloud recording remain the primary path even if IDB/MediaRecorder fails.
      void state.captureController.start({
        audioTrack: state.localAudioTrack,
        videoTrack: state.localVideoTrack
      });
    } else {
      setCaptureState({ state: "disabled" });
    }
    await announceReady();
    await refreshRoomStatus();
  } catch (error) {
    const admissionDenied = String(error.code || "").startsWith("CALL_ADMISSION_");
    const code = admissionDenied ? error.code : reportClientDiagnostic(stage, error);
    await cleanupMedia();
    if (error.code === "CALL_ADMISSION_INVITATION_PENDING") {
      showView("lobby");
      await refreshRoomTrial().catch(() => undefined);
      return;
    }
    showFatalError(admissionDenied ? error.message : callStartFailureMessage(stage, code), { retryCall: true });
  } finally {
    nodes.enterCall.disabled = state.view === "lobby" && state.lobbyBlocked;
  }
}

async function cleanupMedia({
  purgeCapture = false,
  captureStopPromise = null
} = {}) {
  audioQualityReporter.stop();
  clearInterval(state.durationTimer);
  clearInterval(state.heartbeatTimer);
  clearInterval(state.statusTimer);
  clearTimeout(state.reconnectTimer);
  state.durationTimer = null;
  state.heartbeatTimer = null;
  state.statusTimer = null;
  state.reconnectTimer = null;
  state.backgroundedAt = null;
  state.videoSuspendedForBackground = false;
  state.videoSuspendPromise = null;
  state.foregroundRecoveryPromise = null;
  const captureController = state.captureController;
  state.captureController = null;
  if (captureController) {
    let timeoutId;
    const timeout = new Promise((resolve) => {
      timeoutId = setTimeout(resolve, 1800);
    });
    await Promise.race([
      captureStopPromise || captureController.stop({ purge: purgeCapture }),
      timeout
    ]).catch(() => undefined);
    clearTimeout(timeoutId);
  }
  state.localAudioTrack?.stop();
  state.localVideoTrack?.stop();
  state.localAudioTrack?.close();
  state.localVideoTrack?.close();
  state.localAudioTrack = null;
  state.localVideoTrack = null;
  state.callActive = false;
  state.autoplayBlocked = false;
  nodes.resumeMedia.hidden = true;
  try {
    await state.client?.leave();
  } catch {
    // Leaving is best-effort after media has already been stopped.
  }
  state.client = null;
  nodes.localPlayer.replaceChildren();
  nodes.remotePlayers.replaceChildren();
  state.remoteVideoUids.clear();
  state.remoteAudioUids.clear();
  state.remoteVolumeLevels.clear();
}

async function endCall({
  title = "通话已结束",
  message = "",
  notifyLeave = true,
  purgeCapture = false
} = {}) {
  if (state.leaving) return;
  state.leaving = true;
  // Stop the cloned local recorder immediately on the user's tap. The RTC
  // source tracks remain live only while the server receives the leave signal.
  const captureStopPromise = state.captureController?.stop({
    purge: purgeCapture
  }) || null;
  if (notifyLeave) {
    try {
      await api("/api/presence/leave", {
        method: "POST",
        body: "{}",
        keepalive: true
      });
    } catch {
      // The server heartbeat timeout remains the fallback.
    }
  }
  await cleanupMedia({ purgeCapture, captureStopPromise });
  const recordingState = nodes.recordingBadge.dataset.state;
  const showProcessingEstimate = shouldShowPostCallProcessingEstimate({
    recordingStatus: recordingState,
    hasExplicitMessage: Boolean(message)
  });
  if (!message) {
    if (recordingState === "degraded") {
      message = "这段通话正在保存，但录制曾中断，内容可能不完整。";
    } else {
      message = ["starting", "recording", "saving", "completed"].includes(
        recordingState
      )
        ? "这段通话正在安全保存，稍后会出现在回忆里。"
        : "本次通话尚未开始录制。";
    }
  }
  nodes.endedTitle.textContent = title;
  nodes.endedMessage.textContent = message;
  nodes.endedEstimate.textContent = POST_CALL_PROCESSING_ESTIMATE;
  nodes.endedEstimate.hidden = !showProcessingEstimate;
  showView("ended");
}

function trialDate(value) {
  return new Date(Date.parse(value) + 8 * 3600_000).toISOString().slice(0, 16).replace("T", " ");
}

async function refreshRoomTrial() {
  if (!nodes.roomTrial) return;
  nodes.enterCall.disabled = true;
  let result;
  try { result = await api("/api/room/trial"); }
  catch (error) {
    if (error.status !== 404) throw error;
    nodes.roomTrial.hidden = true; nodes.enterCall.disabled = false; state.lobbyBlocked = false; return;
  }
  state.lobbyBlocked = Boolean(result.invitationPending || result.needsClaim || result.waitingForConsent);
  nodes.lobbyTitle.textContent = result.invitationPending ? "等待亲友接受邀请" : state.lobbyBlocked ? "确认后开始通话" : "可以进入通话了";
  nodes.lobbyHint.textContent = result.invitationPending ? "邀请已发出，你可以留在这里等对方，无需反复退出重进。" :
    "双方准备好后，点下方按钮开启摄像头和麦克风。请保持通话页面打开。";
  nodes.enterCall.textContent = result.invitationPending ? "等待对方接受邀请" : result.waitingForConsent ? "等待双方确认" : "进入视频通话";
  if (nodes.invitationWaiting) {
    nodes.invitationWaiting.hidden = !result.invitationPending;
    nodes.invitationWaiting.textContent = result.waitingMessage || "";
  }
  nodes.roomTrial.hidden = !result.enabled;
  if (!result.enabled) { nodes.enterCall.disabled = state.lobbyBlocked; return; }
  state.trialIsInitiator = result.isInitiator;
  state.trialNeedsConsent = result.needsConsent;
  const changedTerms = result.termsVersion !== state.trialTermsVersion;
  state.trialTermsVersion = result.termsVersion;
  nodes.enterCall.disabled = state.lobbyBlocked;
  const trial = result.trial;
  const requiresAction = result.needsConsent || Boolean(result.isInitiator && trial.canClaim);
  nodes.roomTrialClaim.hidden = !requiresAction;
  nodes.roomTrialAcceptRow.hidden = !requiresAction;
  if (changedTerms) nodes.roomTrialAccept.checked = false;
  nodes.alphaCallLimitNotice.textContent = "免费体验单次最多 30 分钟，实际可用时长以发起方剩余余额为准。";
  nodes.alphaCallLimitNotice.hidden = true;
  if (!result.isInitiator) {
    nodes.roomTrialStatus.textContent = result.needsClaim ? "请发起方先领取免费体验；领取后会自动更新。" : "本次通话只扣发起方时长。";
    if (result.waitingForConsent && !result.needsConsent) nodes.roomTrialStatus.textContent += "正在等待对方确认保存规则，无需退出。";
    nodes.roomTrialTerms.textContent = result.retentionNotice;
    nodes.roomTrialClaim.textContent = "确认本次内容保存规则";
    return;
  }
  state.trialTermsVersion = trial.termsVersion;
  const minutes = Math.floor(trial.availableSeconds / 60), seconds = trial.availableSeconds % 60;
  nodes.roomTrialStatus.textContent = trial.state === "unclaimed" ? "你可领取一次 30 分钟免费体验。" :
    trial.state === "upgrade_available" ? "旧体验可补足到累计 30 分钟，已用时长不重复发放。" :
      trial.state === "expired" ? "免费体验时长已到期。" : "免费体验剩余 " + minutes + " 分钟" + (seconds ? " " + seconds + " 秒" : "") + "。";
  const expiry = trial.canClaim ? trial.claimExpiresAt : trial.expiresAt;
  nodes.roomTrialTerms.textContent = trial.usageNotice + (expiry ? "时长可用至 " + trialDate(expiry) + "（北京时间）。" : "") +
    trial.retentionNotice + (trial.claimUnavailableReason || "");
  nodes.roomTrialClaim.textContent = trial.state === "upgrade_available" ? "同意并补足体验时长" : "同意并领取 30 分钟";
  if (!trial.canClaim) nodes.roomTrialClaim.textContent = "确认本次内容保存规则";
  if (result.waitingForConsent && !result.needsConsent) nodes.roomTrialStatus.textContent += "正在等待对方确认保存规则，无需退出。";
}

async function loadExistingSession() {
  try {
    const me = await api("/api/me");
    state.participant = me.participant;
    applyRoomStatus(me);
    if (state.leaving) return true;
    if (me.roomStatus === "cancelled") {
      await endCall({
        title: "这次邀请已经结束",
        message: "你可以回到小程序，重新发起一次通话。",
        notifyLeave: false
      });
      return true;
    }
    nodes.lobbyIdentity.textContent =
      `你将以“${me.participant.displayName}”进入`;
    showView("lobby");
    await refreshRoomTrial();
    return true;
  } catch (error) {
    if (error.status !== 401) throw error;
    return false;
  }
}

async function initialize() {
  const url = new URL(window.location.href);
  const fragment = new URLSearchParams(url.hash.replace(/^#/, ""));
  state.inviteToken =
    fragment.get("invite") || url.searchParams.get("invite") || "";
  state.bridgeToken = fragment.get("bridge") || "";
  state.resumeToken = fragment.get("resume") || "";
  if (state.inviteToken || state.bridgeToken || state.resumeToken) {
    url.searchParams.delete("invite");
    history.replaceState(null, "", `${url.pathname}${url.search}`);
  }
  try {
    const publicConfig = await api("/api/public-config");
    state.alphaCompleteExperienceEnabled =
      publicConfig.alphaCompleteExperienceEnabled === true;
    state.callMaxSeconds = Number(publicConfig.callMaxSeconds) || null;
    nodes.alphaCallLimitNotice.textContent = alphaCallLimitNotice(
      state.callMaxSeconds
    );
    nodes.alphaCallLimitNotice.hidden =
      !state.alphaCompleteExperienceEnabled ||
      !nodes.alphaCallLimitNotice.textContent;
    if (state.resumeToken) {
      const result = await api("/api/miniprogram/call-sessions/claim", {
        method: "POST",
        body: JSON.stringify({ resume: state.resumeToken })
      });
      state.resumeToken = "";
      state.consentVersion = result.consentVersion;
      state.h5CaptureEnabled = result.h5CaptureEnabled === true;
      state.participant = result.participant;
      nodes.lobbyIdentity.textContent =
        `你将以“${result.participant.displayName}”重新进入`;
      showView("lobby");
      await refreshRoomTrial();
      return;
    }
    if (state.inviteToken) {
      const context = await api("/api/invites/context", {
        method: "POST",
        body: JSON.stringify({
          token: state.inviteToken,
          bridgeToken: state.bridgeToken,
          consentPresentation: "full-purpose-v1"
        })
      });
      if (context.resumeExistingSession === true) {
        state.consentVersion = context.consentVersion;
        state.h5CaptureEnabled = context.h5CaptureEnabled === true;
        if (await loadExistingSession()) {
          sendClientDiagnostic("session_resume", "SESSION_RESUMED");
          return;
        }
        throw new Error("会话已经过期，请回到小程序重新进入这次通话。");
      }
      const purpose = document.querySelector("#consent-purpose");
      if (!purpose || !context.consent.purposeText) throw new Error("请刷新页面后重新确认录制说明。");
      purpose.textContent = context.consent.purposeText;
      const trialRetention = document.querySelector("#trial-retention-notice");
      if (trialRetention) {
        trialRetention.textContent = context.trialRetentionNotice || "";
        trialRetention.hidden = !context.trialRetentionNotice;
      }
      state.consentPurposeHash = context.consent.purposeHash || "";
      state.consentVersion = context.consent.version;
      state.h5CaptureEnabled = context.h5CaptureEnabled === true;
      nodes.consentIdentity.textContent = context.participant.displayName;
      showView("consent");
      return;
    }
    const consent = await api("/api/consent");
    state.consentVersion = consent.version;
    state.h5CaptureEnabled = consent.h5CaptureEnabled === true;
    if (await loadExistingSession()) {
      sendClientDiagnostic("session_resume", "SESSION_RESUMED");
      return;
    }
    showFatalError("邀请链接无效或会话已经过期，请联系发起人获取新链接。");
  } catch (error) {
    showFatalError(error.message);
  }
}

nodes.consentForm.addEventListener("submit", async (event) => {
  event.preventDefault();
  nodes.consentError.textContent = "";
  nodes.consentSubmit.disabled = true;
  try {
    const result = await api("/api/invites/claim", {
      method: "POST",
      body: JSON.stringify({
        token: state.inviteToken,
        bridgeToken: state.bridgeToken,
        accepted: nodes.consentCheck.checked,
        consentVersion: state.consentVersion,
        purposeHash: state.consentPurposeHash
      })
    });
    state.inviteToken = "";
    state.bridgeToken = "";
    state.participant = result.participant;
    nodes.lobbyIdentity.textContent =
      `你将以“${result.participant.displayName}”进入`;
    showView("lobby");
    await refreshRoomTrial();
  } catch (error) {
    if (error.code === "ROOM_CLOSED") {
      void endCall({
        title: "这次邀请已经结束",
        message: "你可以回到小程序，重新发起一次通话。",
        notifyLeave: false
      });
    } else {
      nodes.consentError.textContent = error.message;
    }
  } finally {
    nodes.consentSubmit.disabled = false;
  }
});

nodes.openPrivacy.addEventListener("click", () => {
  const miniProgram = globalThis.wx?.miniProgram;
  if (miniProgram?.navigateTo) {
    miniProgram.navigateTo({
      url: "/pages/privacy/index?focus=call"
    });
  }
});

nodes.roomTrialClaim?.addEventListener("click", async () => {
  if (nodes.roomTrialClaim.disabled) return;
  nodes.roomTrialError.textContent = "";
  if (!nodes.roomTrialAccept.checked) {
    nodes.roomTrialError.textContent = "请先阅读并确认体验与保存规则。"; return;
  }
  nodes.roomTrialClaim.disabled = true;
  try {
    await api("/api/room/trial", {
      method: "POST", body: JSON.stringify({ accepted: true, termsVersion: state.trialTermsVersion })
    });
    await refreshRoomTrial();
  } catch (error) {
    nodes.roomTrialError.textContent = error.message || "暂时无法确认领取结果，请重新打开页面核对。";
  } finally { nodes.roomTrialClaim.disabled = false; }
});

nodes.enterCall.addEventListener("click", startCall);
nodes.retryCall.addEventListener("click", startCall);
nodes.leaveCall.addEventListener("click", () => endCall());
nodes.returnToday.addEventListener("click", () => {
  const miniProgram = globalThis.wx?.miniProgram;
  if (miniProgram?.switchTab) {
    miniProgram.switchTab({
      url: "/pages/home/index",
      fail() {
        if (window.history.length > 1) window.history.back();
      }
    });
    return;
  }
  if (window.history.length > 1) window.history.back();
});
nodes.resumeMedia.addEventListener("click", resumeAllMedia);

nodes.toggleMic.addEventListener("click", async () => {
  state.micEnabled = !state.micEnabled;
  await state.localAudioTrack?.setEnabled(state.micEnabled);
  state.captureController?.setTrackEnabled("audio", state.micEnabled);
  nodes.toggleMic.classList.toggle("off", !state.micEnabled);
  nodes.toggleMic.lastElementChild.textContent = state.micEnabled
    ? "静音"
    : "取消静音";
});

nodes.toggleCamera.addEventListener("click", async () => {
  state.cameraEnabled = !state.cameraEnabled;
  await state.localVideoTrack?.setEnabled(state.cameraEnabled);
  state.captureController?.setTrackEnabled("video", state.cameraEnabled);
  nodes.toggleCamera.classList.toggle("off", !state.cameraEnabled);
  nodes.toggleCamera.lastElementChild.textContent = state.cameraEnabled
    ? "关摄像头"
    : "开摄像头";
});

AgoraRTC.on("autoplay-failed", () => {
  state.autoplayBlocked = true;
  refreshMediaRecoveryButton("浏览器暂停了画面或声音，请点一下恢复。");
});

AgoraRTC.on("audio-context-state-changed", (currentState) => {
  state.audioContextState = currentState;
  if (currentState !== "running") {
    refreshMediaRecoveryButton("声音播放已暂停，请点一下恢复。");
  } else if (!state.autoplayBlocked) {
    nodes.resumeMedia.hidden = true;
  }
});

document.addEventListener("visibilitychange", () => {
  if (!state.client || state.leaving) return;
  state.captureController?.handleVisibilityChange(
    document.visibilityState !== "visible"
  );
  void api("/api/presence/heartbeat", {
    method: "POST",
    body: "{}",
    keepalive: true
  }).catch(() => undefined);
  if (document.visibilityState === "visible") {
    void recoverForegroundMedia();
  } else {
    suspendActiveVideoForBackground();
  }
});

window.addEventListener("online", () => {
  state.captureController?.handleOnline();
  if (document.visibilityState === "visible") {
    void recoverForegroundMedia();
  }
});

window.addEventListener("pagehide", () => {
  suspendActiveVideoForBackground();
  state.captureController?.requestData();
});

window.addEventListener("pageshow", () => {
  if (document.visibilityState === "visible") {
    void recoverForegroundMedia();
  }
});

window.addEventListener("beforeunload", () => {
  if (!state.client || state.leaving) return;
  void fetch("/api/presence/heartbeat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
    credentials: "same-origin",
    keepalive: true
  });
});

globalThis.__LIUZHU_PHASE_B__ = {
  getState() {
    return {
      participant: state.participant,
      connectionState: state.client?.connectionState || "DISCONNECTED",
      recordingState: nodes.recordingBadge.dataset.state,
      localPreviewPresent: Boolean(nodes.localPlayer.querySelector("video")),
      remoteVideoCount: state.remoteVideoUids.size,
      remoteAudioCount: state.remoteAudioUids.size,
      remoteVolumeLevels: Object.fromEntries(state.remoteVolumeLevels),
      remoteAudioTracks: (state.client?.remoteUsers || [])
        .filter((user) => user.audioTrack)
        .map((user) => {
          const mediaTrack = user.audioTrack.getMediaStreamTrack();
          return {
            uid: String(user.uid),
            isPlaying: user.audioTrack.isPlaying,
            configuredVolume: user.audioTrack.getVolume(),
            liveVolume: user.audioTrack.getVolumeLevel(),
            mediaReadyState: mediaTrack.readyState,
            mediaMuted: mediaTrack.muted,
            mediaEnabled: mediaTrack.enabled,
            stats: user.audioTrack.getStats()
          };
        }),
      audioContextState: state.audioContextState,
      autoplayBlocked: state.autoplayBlocked,
      micEnabled: state.micEnabled,
      cameraEnabled: state.cameraEnabled,
      captureState: state.captureState,
      capture: state.captureController?.getSnapshot() || null
    };
  }
};

void initialize();
